import {IEditorFont, IEditorFontService} from "./interfaces";
import {IFontData} from "@/editor/base/editor.interfaces";
import {Font} from "@/editor/base/models-base";


export interface IJsonApi {
        getFontData():Promise<IFontData[]>;
    }

    export interface IFontServiceData {
        fontName: string;
        fontSrc: string;
        public: boolean;
    }

    const FVD_MAP = {'r': 'n4', 'b': 'n7', 'i': 'i4', 'bi': 'i7'};

    export class FontService implements IEditorFontService {

        public static readonly DEFAULT_FONT:string = 'Lato';

        private readonly fontData: IFontServiceData[] = [];
        private readonly fontImages: { [fontName: string]: string } = {};
        private readonly fontsMap: { [fontName: string]: IEditorFont } = {};

        public readonly fonts: IEditorFont[] = [];
        private promises: { [fontName: string]: Promise<void> } = {};
        private promiseComplete: { [fontName: string]: boolean } = {};
        private loadedFonts: boolean = false;
        private fontDataLoaded: Promise<void>|null = null;

        public constructor(private jsonApi: IJsonApi) {

            this.clearVariables();
        }

        public releaseFontService():void {
            this.clearVariables();
        }

        public fontsLoaded():Promise<void>|null {
            return this.fontDataLoaded;
        }

        public getFontImageCollection(): Promise<{ [fontName: string]: string }> {
            // What are we doing here?  The options won't show up until all the images are loaded, and they must be loaded
            // before we show the dialog so we create an array of promises to ensure we have preloaded the images...
            return new Promise<{ [fontName: string]: string }>(
                (resolve: (imageCollection: { [fontName: string]: string }) => void): void => {
                    if (Object.keys(this.fontImages).length === 0) {
                        const promises: Promise<void>[] = [];
                        for (let i = 0; i < this.fontData.length; i++) {
                            if (this.fontData[i].public) {
                                const fontImage = this.fontData[i].fontSrc;
                                this.fontImages[this.fontData[i].fontName] = `<div class="font-image-holder"><img alt="${this.fontData[i].fontName}" src="${fontImage}"/></div>`;

                                promises.push(new Promise<void>((loadImageResolve: () => void): void => {
                                    const img = new Image();
                                    img.onload = (): void => {
                                        loadImageResolve();
                                    };
                                    img.onerror = (): void => {
                                        delete this.fontImages[this.fontData[i].fontName];    // This image didn't load, don't show it...
                                        loadImageResolve();
                                    };
                                    img.src = fontImage;
                                }));
                            }
                        }
                        Promise.all(promises).then((): void => {
                            resolve(this.fontImages);
                        },error => {
                            console.error('Error getting font images', error);
                            resolve(this.fontImages);
                        });
                    } else {
                        resolve(this.fontImages);
                    }
                });

        }

        public getPublicFonts():Promise<IFontServiceData[]> {
            return new Promise<IFontServiceData[]>((resolve: (fonts:IFontServiceData[]) => void): void => {
               const returnFonts = ():void => {
                   if (this.loadedFonts) {
                       resolve(this.fontData.filter(font => font.public));
                   } else {
                       setTimeout(() => {
                           returnFonts();
                       }, 50);
                   }
                };
               returnFonts();
            });
        }

        public getFont(fontName: string): IFontServiceData|null {
            return this.fontData.find(font => font.fontName === fontName) || null;
        }

        public loadFontFile(): Promise<void> {
            this.fontDataLoaded = new Promise<void>((resolve: () => void, reject: (error: string) => void): void => {
                if (!this.loadedFonts) {
                    this.loadedFonts = true;
                    this.jsonApi.getFontData().then((data: IFontData[]): void => {
                        data.forEach(font => {
                            const fontData = {fontName: font.name, fontSrc: font.image, public: font.public};
                            Object.freeze(fontData);
                            this.fontData.push(fontData);
                            const tmpFont = new Font(font);
                            this.fontsMap[font.name] = tmpFont;
                            this.fonts.push(tmpFont);
                            this.fontData.sort((a, b) => a.fontName.localeCompare(b.fontName));
                        });
                        resolve();
                    }, err => {
                        reject(err);
                        console.error('Error parsing json ', err);
                        // TODO: Show error...
                    });

                } else {
                    resolve();
                }
            });
            return this.fontDataLoaded;
        }

        public get(name: string): IEditorFont|null {
            return this.convertToFont(name);
        }

        private clearVariables():void {
            this.promises = {};
            this.promiseComplete = {};
            for (let i = 0; i < this.fonts.length; i++) {
                this.fonts[i].openedFont = {
                    b: null,
                    i: null,
                    bi: null,
                    r: null
                };
            }
        }

        public isLoaded(name: string, style: string): boolean {
            const cacheKey = this.getCacheKey(name, style);

            return this.promises.hasOwnProperty(cacheKey) && this.promiseComplete[cacheKey];
        }

        public areLoaded(fontDescriptions: {[fontFamily:string]:string[]}): boolean {
            const families = Object.keys(fontDescriptions);

            for (let i = 0; i < families.length; i++) {
                const family = families[i];

                for (let j = 0; j < fontDescriptions[family].length; j++) {
                    if (!this.isLoaded(family, fontDescriptions[family][j])) {
                        return false;
                    }
                }
            }

            return true;
        }

        public load(font: IEditorFont, style: string): Promise<void> {
            const cacheKey = this.getCacheKey(font.name, style);

            // if the promise doesn't exist, create one
            if (!this.promises.hasOwnProperty(cacheKey)) {

                const cssAndWoffPromises: Promise<void>[] = [];
                const webFontPromise = new Promise<void>((resolve:() => void, reject:(err:any) => void):void => {

                    window.WebFont.load({
                        custom: {
                            families: [font.name + ':' + this.convertStyleToFVD(style)],
                            urls: [ font.getCSS()]
                        },
                        fontactive: ():void => {
                            resolve();
                        },
                        fontinactive: (): void => {
                            reject('fontinactive');
                        },
                        timeout: 5000

                    });
                });

                cssAndWoffPromises.push(webFontPromise);

                //the font is not "ready" until we have the css and have opened it
                this.promiseComplete[cacheKey] = false;
                this.promises[cacheKey] = Promise.all(cssAndWoffPromises).then(() => {
                    this.promiseComplete[cacheKey] = true;
                }, err => {
                    this.promiseComplete[cacheKey] = false;
                    console.error('Unable to load font: ' + err);
                });
            }

            return this.promises[cacheKey];
        }

        public loadMultiple(fontDescriptions: {[familyName:string]:string[]}): Promise<any> {
            this.preLoad(fontDescriptions);
            const promiseArray = Object.keys(fontDescriptions).map((family: string): Promise<void>[] => {
                return fontDescriptions[family].map((style: string): Promise<void> => {
                    return this.promises[this.getCacheKey(family, style)];
                });
            });
            return Promise.all(promiseArray.flat());
        }

        public preLoad(fontDescriptions: {[familyName:string]:string[]}): void {
            let font:IEditorFont|null;
            let cacheKey;
            const cacheKeys: string[] = [];
            const families: string[] = [];
            const urls: string[] = [];

            // figure out what to load
            Object.keys(fontDescriptions).forEach((name: string): void => {
                // convert to font
                font = this.convertToFont(name);

                // stop if font not found
                if (!font) {
                    return;
                }

                // figure out which styles to load
                const stylesToLoad: string[] = [];

                fontDescriptions[name].forEach((style: string): void => {
                    cacheKey = this.getCacheKey((font as IEditorFont).name, style);

                    if (!this.promises.hasOwnProperty(cacheKey)) {
                        stylesToLoad.push(style);
                        cacheKeys.push(cacheKey);
                    }
                });

                // if all the styles requested for this font are already loaded / loading, skip
                if (stylesToLoad.length === 0) {
                    return;
                }

                // append data to arrays
                families.push(font.name + ':' + stylesToLoad.map(this.convertStyleToFVD).join(','));
                urls.push(font.getCSS());
            });

            // if there are fonts to pre-load, do so here
            if (cacheKeys.length > 0) {
                const cssAndWoffPromises: Promise<void>[] = [];

                const webFontPromise = new Promise<void>((resolve:() => void, reject:(error:any) => void):void => {
                    window.WebFont.load({
                        custom: {families, urls},
                        fontactive: ():void => {
                            console.debug('Loaded fonts ', families);
                            resolve();
                        },
                        fontinactive: (): void => {
                            reject('fontinactive');
                        },
                        timeout: 5000
                    });
                });

                cssAndWoffPromises.push(webFontPromise);

                cacheKeys.forEach((cacheKeyValue: string): void => {
                    //the font is not "ready" until we have the css and have opened it
                    this.promises[cacheKeyValue] = Promise.all(cssAndWoffPromises).then(() => {
                        this.promiseComplete[cacheKeyValue] = true;
                    }, err => {
                        console.error('Unable to load font: ' +  cacheKeyValue + ', err: ' + err);
                    });
                });

            }
        }

        public getFirstLoadedFont():Promise<string|null> {
            return new Promise((resolve:(font:string|null) => void):void => {
                const allFonts = Object.keys(this.fontsMap);

                for (let i = 0; i < allFonts.length; i++) {
                    const cacheKey = this.getCacheKey(allFonts[i], 'r');
                    if (this.promises.hasOwnProperty(cacheKey) && this.promiseComplete[cacheKey]) {
                        resolve(allFonts[i]);
                    }

                }
                resolve(null);
            });
        }

        // private
        private convertToFont(font: string|IEditorFont): IEditorFont|null {
            if (typeof(font) === 'string') {
                font = this.fontsMap.hasOwnProperty(font) ? this.fontsMap[font] : this.fontsMap[FontService.DEFAULT_FONT];
            }

            return font;
        }

        private getCacheKey(name: string, style: string): string {
            return name + '-' + (style ? style : 'r');
        }

        private convertStyleToFVD(style: string): string {
            return (FVD_MAP as any)[style] || 'n4';
        }

        public fontsMapLoaded(retryCount: number = 0): Promise<void> {
            return new Promise<any>((resolve:(result: void) => void, reject:(error:string) => void):void => {
                if (Object.keys(this.fontsMap).length > 0) {
                    resolve();
                } else {
                    if (retryCount > 20) {
                        reject('fontsMapLoaded timeout');
                    }
                    retryCount ++;
                    setTimeout(() => {
                        this.fontsMapLoaded(retryCount).then(resolve, reject);
                    }, 100);
                }
            });
        }
    }